import { type PackArrayKey, type PackAuthor } from '@/entities/content'

/**
 * A single custom (non-SRD) content entity a character references, captured at
 * save time so it can be named even after its pack is gone. See
 * `utils/characterContent.ts`.
 */
export interface ContentEntryRef {
  type: PackArrayKey
  id: string
  /** Readable title of the entry (e.g. "Stormborn Sorcerer"). */
  name: string
}

/** The custom content one imported pack contributes to a character. */
export interface PackContentRef {
  /** Pack id: the imported pack's stable identifier (its "file name"). */
  packId: string
  /** Readable pack title. */
  packName: string
  /** Who made the pack, kept so a character opened without it can say whom to ask. Untrusted: re-check before linking. */
  author?: PackAuthor
  entries: ContentEntryRef[]
}

export interface AbilityScores {
  str: number
  dex: number
  con: number
  int: number
  wis: number
  cha: number
}

export type Skill =
  | 'acrobatics'
  | 'animalHandling'
  | 'arcana'
  | 'athletics'
  | 'deception'
  | 'history'
  | 'insight'
  | 'intimidation'
  | 'investigation'
  | 'medicine'
  | 'nature'
  | 'perception'
  | 'performance'
  | 'persuasion'
  | 'religion'
  | 'sleightOfHand'
  | 'stealth'
  | 'survival'

/** The SRD 5.2.1 coins, by their standard abbreviations. */
export type Coin = 'pp' | 'gp' | 'ep' | 'sp' | 'cp'

/** How many of each coin a character carries; an absent coin means none. */
export type Purse = Partial<Record<Coin, number>>

/**
 * One entry in the sheet's "Loot & Keepsakes": something the player wrote down
 * themselves — treasure, a keepsake, a plot item — as opposed to an entry from
 * the SRD or a content pack. Lives on the character rather than in the content,
 * so it travels with them.
 */
export interface LootItem {
  /** Stable local id; only ever referenced from this character. */
  id: string
  /** Required — an item with no name can't be told apart in the list. */
  name: string
  description?: string
  /**
   * Worth *or* amount, exactly as the player wrote it: "50gp", "priceless",
   * "two cows", or simply "3" for three of them. Free text on purpose — one
   * short line covers both questions without forcing a choice up front.
   */
  value?: string
  /** Coin amounts when `value` happens to read as currency; absent when it doesn't. */
  valueCoins?: Purse
}

export type AbilityScoreMethod = 'standardArray' | 'pointBuy' | 'manual'

export interface CharacterTraits {
  personality: string
  ideals: string
  bonds: string
  flaws: string
}

export interface Character {
  name: string
  playerName: string
  xp: number
  speciesId: string
  subspeciesId: string
  /**
   * Every class this character holds, in the order taken. `classes[0]` is the
   * "starting class": it alone grants the full starting-equipment package and
   * the character's one total-level-1 full Hit Die (SRD 5.2.1 Multiclassing).
   * Total character level is never stored — see `totalLevel`.
   */
  classes: { classId: string; level: number }[]
  backgroundId: string
  baseScores: AbilityScores
  abilityScoreMethod: AbilityScoreMethod
  /**
   * SRD 5.2.1: ability score increases granted by the background, allocated by
   * the player across the background's three abilities (one +2 & one +1, or
   * three +1). Applied on top of baseScores by `finalScores`.
   */
  backgroundAbilityBonuses: Partial<AbilityScores>
  /** Ability score increases accumulated from level-ups (ASI feat choices). */
  levelBonuses: Partial<AbilityScores>
  /** Skill proficiencies picked at each held class's own skill-choice grid, keyed by that class's id. */
  skillProficiencies: Record<string, Skill[]>
  /** Origin feats chosen beyond the one the background grants (e.g. Human's Versatile trait). */
  featIds: string[]
  /** Level-1 class choices, keyed by ClassChoice id → selected option ids/values. */
  classChoices: Record<string, string[]>
  /** Chosen starting-equipment package index (into the class's `startingEquipmentOptions`). */
  classEquipmentChoice: number
  /** Chosen starting-equipment package index (into the background's `equipmentChoices`). */
  backgroundEquipmentChoice: number
  equipment: string[]
  /** Chosen cantrips (spell ids, level 0). */
  cantrips: string[]
  /** Chosen level 1+ spells (spell ids). */
  preparedSpells: string[]
  /**
   * Play-state: current hit points. `undefined` means "full" (computed max HP).
   * Persisted with the character so it survives save/download/upload.
   */
  currentHp?: number
  /**
   * Play-state: temporary hit points (a separate pool that has no upper bound and
   * is not capped by max HP). Persisted alongside {@link currentHp}.
   */
  temporaryHp?: number
  /** Play-state: Death Saving Throw marks while at 0 HP. Cleared when hit points are regained. */
  deathSaves?: { successes: number; failures: number }
  /**
   * Play-state: spent spell slots, keyed by slot level → number used. Persisted
   * with the character alongside {@link currentHp}.
   */
  usedSpellSlots?: Record<number, number>
  /**
   * By spell id, the held class a cantrip or prepared spell was taken as, for a spell more than one
   * held class lists (a Cleric / Wizard's Light). Absent: the first held class that lists it.
   */
  spellOwners?: Record<string, string | string[]>
  /** Play-state: Hit Point Dice spent, by die size (`{ 10: 2 }` = two d10s). */
  usedHitDice?: Record<number, number>
  /** Play-state: spent Pact Magic slots, tracked separately from {@link usedSpellSlots} only when Pact Magic (Warlock) coexists with another spellcasting class — otherwise Warlock's own slots use usedSpellSlots as today. */
  usedPactSlots?: Record<number, number>
  /** Play-state: granted spells whose once-per-rest slot-free cast is spent (spell ids). */
  usedFreeCasts?: string[]
  /**
   * Play-state: current value of each class resource (e.g. Sorcery Points, Rage),
   * keyed by the resource key from `src/data/classResources.ts`. `undefined` for a
   * key means "full" (the derived max). Persisted alongside {@link currentHp}.
   */
  classResources?: Record<string, number>
  /**
   * Play-state: ids of equipment the character has "activated" (worn/wielded).
   * Worn armor and a wielded shield drive the derived AC (`computeAC`); at most
   * one armor and one shield are meaningful at a time. A subset of {@link equipment}.
   */
  equippedIds?: string[]
  /** Play-state: coins carried. Absent (or an absent coin) means none. */
  currency?: Purse
  /** "Loot & Keepsakes": items the player added by hand, not from any content pack. */
  loot?: LootItem[]
  /** Play-state: whether the character currently has Heroic Inspiration. */
  heroicInspiration?: boolean
  /** Play-state: active conditions (standard condition names). */
  conditions?: string[]
  /**
   * Tool proficiencies the player picked to satisfy a "Choose N …" grant, keyed
   * by the raw grant string (e.g. `"Choose 3 Musical Instruments"`) → chosen tools.
   */
  toolChoices?: Record<string, string[]>
  /** Languages the character speaks (beyond Common, which everyone knows). */
  languages?: string[]
  /** Free-text damage defenses (resistances / immunities / vulnerabilities). */
  defenses?: string
  /** Character portrait: an image URL or an uploaded `data:` URL. */
  avatar?: string
  traits: CharacterTraits
  appearance: string
  backstory: string
  /**
   * Provenance of the custom (non-SRD) content this character uses, grouped by
   * imported pack. Written whenever the character is saved to the browser or
   * downloaded so that, on load, missing content (a whole pack or specific
   * entries) can be listed by name even though the entities can no longer be
   * resolved. Not a derived stat: it's file metadata captured at save time.
   * See `utils/characterContent.ts`.
   */
  contentRefs?: PackContentRef[]
  /**
   * The shape of this JSON (`CHARACTER_SCHEMA_VERSION` when written): which numbered
   * migrations in `lib/migrate.ts` it has been through. Absent means before they existed.
   */
  schemaVersion?: number
  /**
   * The rules this character was last checked against (`rulesStamp`: the app version and
   * the packs it uses). When it differs from today's, the character page checks what it
   * owes under the current rules before showing the sheet.
   */
  rulesStamp?: string
}

/** The current {@link Character.schemaVersion}: one per migration in `lib/migrate.ts`. */
export const CHARACTER_SCHEMA_VERSION = 3

export const EMPTY_CHARACTER: Character = {
  schemaVersion: CHARACTER_SCHEMA_VERSION,
  name: '',
  playerName: '',
  xp: 0,
  speciesId: '',
  subspeciesId: '',
  classes: [{ classId: '', level: 1 }],
  backgroundId: '',
  baseScores: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 },
  abilityScoreMethod: 'standardArray',
  backgroundAbilityBonuses: {},
  levelBonuses: {},
  skillProficiencies: {},
  featIds: [],
  classChoices: {},
  classEquipmentChoice: 0,
  backgroundEquipmentChoice: 0,
  equipment: [],
  cantrips: [],
  preparedSpells: [],
  usedSpellSlots: {},
  classResources: {},
  equippedIds: [],
  heroicInspiration: false,
  conditions: [],
  toolChoices: {},
  languages: [],
  defenses: '',
  avatar: '',
  traits: { personality: '', ideals: '', bonds: '', flaws: '' },
  appearance: '',
  backstory: '',
}

export const SKILL_ABILITY_MAP: Record<Skill, keyof AbilityScores> = {
  acrobatics: 'dex',
  animalHandling: 'wis',
  arcana: 'int',
  athletics: 'str',
  deception: 'cha',
  history: 'int',
  insight: 'wis',
  intimidation: 'cha',
  investigation: 'int',
  medicine: 'wis',
  nature: 'int',
  perception: 'wis',
  performance: 'cha',
  persuasion: 'cha',
  religion: 'int',
  sleightOfHand: 'dex',
  stealth: 'dex',
  survival: 'wis',
}

export const SKILL_LABELS: Record<Skill, string> = {
  acrobatics: 'Acrobatics',
  animalHandling: 'Animal Handling',
  arcana: 'Arcana',
  athletics: 'Athletics',
  deception: 'Deception',
  history: 'History',
  insight: 'Insight',
  intimidation: 'Intimidation',
  investigation: 'Investigation',
  medicine: 'Medicine',
  nature: 'Nature',
  perception: 'Perception',
  performance: 'Performance',
  persuasion: 'Persuasion',
  religion: 'Religion',
  sleightOfHand: 'Sleight of Hand',
  stealth: 'Stealth',
  survival: 'Survival',
}

export const ABILITY_LABELS: Record<keyof AbilityScores, string> = {
  str: 'Strength',
  dex: 'Dexterity',
  con: 'Constitution',
  int: 'Intelligence',
  wis: 'Wisdom',
  cha: 'Charisma',
}
